import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { AoSGameState } from '../../types';
import { undoToTurnStart } from '../moves/undoToTurnStart';

/**
 * Phase 6: 収入獲得フェーズ
 *
 * 各プレイヤーは収入レベルに応じた収入を自動的に獲得します。
 * ロジック処理後、ユーザーがクリックするまで待機します。
 *
 * 処理内容:
 * - プレイヤーの収入レベルに応じた収入を獲得
 * - 基本ルール: incomeLevel の値がそのまま収入額
 * - 収入が負の場合は$0獲得（現金は減少しない）
 * - 倒産プレイヤーは処理をスキップ
 *
 * 参照:
 * - docs/specifications/logic/common/phase_06_collect_income.md
 * - docs/specifications/logic/barbados/phase_06_collect_income_override.md
 */
export const collectIncomePhase: PhaseConfig<AoSGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[collectIncomePhase] ★★★ Phase 6: Collect Income started ★★★');
    debug.log('[collectIncomePhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase, currentPlayer: ctx.currentPlayer });

    // ソロプレイ用: Player 0の収入を計算
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 収入計算: incomeLevel の値がそのまま収入額
    // 負の場合は$0獲得（現金は減少しない）
    const income = Math.max(0, player.incomeLevel);
    player.cash += income;

    debug.log(`[collectIncomePhase] Player 0 collected $${income} (incomeLevel: ${player.incomeLevel}, cash: ${player.cash})`);

    // 処理結果を保存（UI表示用）
    G.autoPhaseResult = {
      phase: 'collectIncome',
      message: 'Collecting income based on income level...',
      floatingText: income > 0 ? `+$${income}` : '$0',
      details: { income },
    };

    debug.log('[collectIncomePhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: AoSGameState; events: any }) => {
      // Phase内でのみ呼び出し可能（Boardgame.ioが保証）
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: AoSGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[collectIncomePhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    // 倒産時は即座に終了
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G, ctx }: { G: AoSGameState; ctx: any }) => {
    debug.log('[collectIncomePhase] ★★★ onEnd called - Phase 6 ended ★★★');
    debug.log('[collectIncomePhase] ★★★ ctx:', { turn: ctx.turn, phase: ctx.phase });
    debug.log('[collectIncomePhase] ★★★ Next phase should be: payExpenses');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('collectIncome');
  },

  // Phase 7 へ遷移、または Undo 時は issueShares へ
  next: ({ G }: { G: AoSGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'payExpenses';
  }
};
